import { createFeatureSelector, createSelector } from "@ngrx/store";

import { DISTANCE_LIMIT_BEFORE_WARNING } from "../constants/shop-locator.constant";
import { shopLocatorFeatureKey } from "../reducers/shop-locator.reducer";
import {
    ShopLocatorState,
    ShopListState,
    SelectedLocationState,
    ShopLocatorExtraInfos,
    ShopsStateData,
} from "../reducers/shop-locator.state";

export const selectShopLocatorState = createFeatureSelector<ShopLocatorState>(
    shopLocatorFeatureKey,
);

//
// Shop list selectors :
//
export const selectShopsListState = createSelector(
    selectShopLocatorState,
    (shopLocator: ShopLocatorState) => shopLocator.shopList,
);

export const selectShops = createSelector(
    selectShopsListState,
    (shopsList: ShopListState) => shopsList.shops,
);

export const selectShopsListHasError = createSelector(
    selectShopsListState,
    (shopsList: ShopListState) =>
        !!(shopsList.error && shopsList.error.length > 0),
);

export const selectShopsListLoading = createSelector(
    selectShopsListState,
    (shopsList: ShopListState) => shopsList.status === "loading",
);

export const selectShopsListLoaded = createSelector(
    selectShopsListState,
    (shopsList: ShopListState) => shopsList.status === "loaded",
);

export const selectShopsListStatus = createSelector(
    selectShopsListState,
    (shopsList: ShopListState) => shopsList.status,
);

export const selectHasShops = createSelector(
    selectShopsListState,
    (shopsList: ShopListState) =>
        shopsList.shops && Object.keys(shopsList.shops).length > 0,
);

export const selectShopsParameters = createSelector(
    selectShopsListState,
    (shopsList: ShopListState) => shopsList.parameters,
);

//
// Selected location selector :
//
export const selectSelectedLocation = createSelector(
    selectShopLocatorState,
    (shopLocatorState: ShopLocatorState) => shopLocatorState.selectedLocation,
);

//
// Hovered Shop selectors :
//
export const selectHoveredShop = createSelector(
    selectShopLocatorState,
    (shopLocatorState: ShopLocatorState) => shopLocatorState.hoveredShop,
);

export const selectHoveredShopCoordinates = createSelector(
    selectShopLocatorState,
    (shopLocatorState: ShopLocatorState) =>
        shopLocatorState.hoveredShop === "NO_HOVERED_SHOP"
            ? undefined
            : shopLocatorState.hoveredShop.coordinates,
);

export const selectHoveredShopId = createSelector(
    selectShopLocatorState,
    (shopLocatorState: ShopLocatorState) =>
        shopLocatorState.hoveredShop === "NO_HOVERED_SHOP"
            ? undefined
            : shopLocatorState.hoveredShop.shopId,
);

//
// Visible shops selector :
//
export const selectVisibleShopIds = createSelector(
    selectShopLocatorState,
    (shopLocatorState: ShopLocatorState) =>
        shopLocatorState.items.visibleShopIds,
);

export const selectShopIdsToIgnore = createSelector(
    selectShopLocatorState,
    (shopLocatorState: ShopLocatorState) =>
        shopLocatorState.items.shopIdsToIgnore,
);

//
// Extra information about shop selector :
//
export const selectExtraInfos = createSelector(
    selectShopLocatorState,
    (shopLocatorState: ShopLocatorState) => shopLocatorState.items.extraInfos,
);

//
// Configs selectors :
//
export const selectMode = createSelector(
    selectShopLocatorState,
    (shopLocatorState: ShopLocatorState) => shopLocatorState.config.mode,
);

export const selectDisplayWarningMessage = createSelector(
    selectSelectedLocation,
    selectVisibleShopIds,
    selectExtraInfos,
    (
        selectedLocation: SelectedLocationState,
        visibleShopIds: string[],
        extraInfos: ShopLocatorExtraInfos,
    ) =>
        selectedLocation !== "NO_SELECTED_LOCATION" &&
        !selectedLocation.isCountry &&
        visibleShopIds.length > 0 &&
        Object.keys(extraInfos).length > 0 &&
        extraInfos[visibleShopIds[0]].meters > DISTANCE_LIMIT_BEFORE_WARNING,
);

export const selectShopsWithIgnored = createSelector(
    selectShops,
    selectShopIdsToIgnore,
    (shops, shopIdsToIgnore): ShopsStateData => {
        if (shopIdsToIgnore.length === 0) {
            return shops;
        }
        const shopsWithIgnored: ShopsStateData = {};
        for (const shopId in shops) {
            if (!shopIdsToIgnore.includes(shopId)) {
                shopsWithIgnored[shopId] = shops[shopId];
            }
        }
        return shopsWithIgnored;
    },
);
